// Straight Line Equations Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const straightLineEquationsSamples = {
  fundamental: [
    {
      id: 'line_fund_01',
      problem: "Find the equation of a line with slope 2 and y-intercept 3",
      problemType: "straight_line_equations",
      difficulty: "fundamental",
      description: "Basic slope-intercept form",
      graphData: {
        nodes: [
          {
            id: "slope",
            name: "Slope",
            type: "concepts",
            description: "The slope of the line",
            value: "m = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "y_intercept",
            name: "Y-Intercept",
            type: "concepts",
            description: "The y-intercept of the line",
            value: "b = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "slope_intercept_formula",
            name: "Slope-Intercept Formula",
            type: "formulas",
            description: "Standard form: y = mx + b",
            value: "y = mx + b",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_values",
            name: "Step 1: Identify Values",
            type: "steps",
            description: "Identify slope and y-intercept from given information",
            value: "Step 1: m = 2, b = 3",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Substitute values into slope-intercept formula",
            value: "Step 2: y = 2x + 3",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_verify_result",
            name: "Step 3: Verify Result",
            type: "steps",
            description: "Check that the equation is in correct form",
            value: "Step 3: y = 2x + 3 ✓",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the line",
            value: "y = 2x + 3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "slope", to: "slope_intercept_formula", type: "used in", strength: 9 },
          { from: "y_intercept", to: "slope_intercept_formula", type: "used in", strength: 9 },
          { from: "slope", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "y_intercept", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "slope_intercept_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_values", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_verify_result", type: "leads to", strength: 10 },
          { from: "step3_verify_result", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_fund_02',
      problem: "Find the slope of a line passing through points (2, 3) and (4, 7)",
      problemType: "straight_line_equations",
      difficulty: "fundamental",
      description: "Basic slope calculation",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point on the line",
            value: "(2, 3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point on the line",
            value: "(4, 7)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "slope_formula",
            name: "Slope Formula",
            type: "formulas",
            description: "Slope between two points: m = (y₂ - y₁)/(x₂ - x₁)",
            value: "m = (y₂ - y₁)/(x₂ - x₁)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_coordinates",
            name: "Step 1: Identify Coordinates",
            type: "steps",
            description: "Extract coordinates from points",
            value: "Step 1: (x₁, y₁) = (2, 3), (x₂, y₂) = (4, 7)",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Substitute coordinates into slope formula",
            value: "Step 2: m = (7 - 3)/(4 - 2) = 4/2 = 2",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_verify_result",
            name: "Step 3: Verify Result",
            type: "steps",
            description: "Check that slope is a real number",
            value: "Step 3: m = 2 ✓",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The slope of the line",
            value: "2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "slope_formula", type: "used in", strength: 9 },
          { from: "point2", to: "slope_formula", type: "used in", strength: 9 },
          { from: "point1", to: "step1_identify_coordinates", type: "used in", strength: 9 },
          { from: "point2", to: "step1_identify_coordinates", type: "used in", strength: 9 },
          { from: "slope_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_coordinates", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_verify_result", type: "leads to", strength: 10 },
          { from: "step3_verify_result", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_fund_03',
      problem: "Find the equation of a line passing through point (1, 2) with slope 3",
      problemType: "straight_line_equations",
      difficulty: "fundamental",
      description: "Point-slope form",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Point on the line",
            value: "(1, 2)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "slope",
            name: "Slope",
            type: "concepts",
            description: "Slope of the line",
            value: "m = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_values",
            name: "Step 1: Identify Values",
            type: "steps",
            description: "Extract point coordinates and slope",
            value: "Step 1: (x₁, y₁) = (1, 2), m = 3",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Substitute values into point-slope formula",
            value: "Step 2: y - 2 = 3(x - 1)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Expand and simplify to slope-intercept form",
            value: "Step 3: y - 2 = 3x - 3, y = 3x - 1",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the line",
            value: "y = 3x - 1",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "slope", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "point", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "slope", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "point_slope_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_values", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_simplify", type: "leads to", strength: 10 },
          { from: "step3_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_fund_04',
      problem: "Find the x-intercept of the line y = 2x + 4",
      problemType: "straight_line_equations",
      difficulty: "fundamental",
      description: "Basic x-intercept calculation",
      graphData: {
        nodes: [
          {
            id: "line_equation",
            name: "Line Equation",
            type: "concepts",
            description: "The equation of the line",
            value: "y = 2x + 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "x_intercept_condition",
            name: "X-Intercept Condition",
            type: "concepts",
            description: "At x-intercept, y = 0",
            value: "y = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "x_intercept_formula",
            name: "X-Intercept Formula",
            type: "formulas",
            description: "Set y = 0 and solve for x",
            value: "y = 0 → 0 = 2x + 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_set_y_zero",
            name: "Step 1: Set y = 0",
            type: "steps",
            description: "Substitute y = 0 into the equation",
            value: "Step 1: 0 = 2x + 4",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_solve_for_x",
            name: "Step 2: Solve for x",
            type: "steps",
            description: "Solve the linear equation for x",
            value: "Step 2: 2x = -4, x = -2",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_write_point",
            name: "Step 3: Write Point",
            type: "steps",
            description: "Express as coordinate point",
            value: "Step 3: (-2, 0)",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The x-intercept",
            value: "(-2, 0)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line_equation", to: "x_intercept_formula", type: "used in", strength: 9 },
          { from: "x_intercept_condition", to: "x_intercept_formula", type: "used in", strength: 9 },
          { from: "line_equation", to: "step1_set_y_zero", type: "used in", strength: 9 },
          { from: "x_intercept_condition", to: "step1_set_y_zero", type: "used in", strength: 9 },
          { from: "x_intercept_formula", to: "step2_solve_for_x", type: "used in", strength: 10 },
          { from: "step1_set_y_zero", to: "step2_solve_for_x", type: "leads to", strength: 10 },
          { from: "step2_solve_for_x", to: "step3_write_point", type: "leads to", strength: 10 },
          { from: "step3_write_point", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_fund_05',
      problem: "Find the equation of a horizontal line passing through point (3, 5)",
      problemType: "straight_line_equations",
      difficulty: "fundamental",
      description: "Horizontal line equation",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Point on the line",
            value: "(3, 5)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "horizontal_line_property",
            name: "Horizontal Line Property",
            type: "concepts",
            description: "Horizontal lines have equation y = k",
            value: "y = constant",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "horizontal_line_formula",
            name: "Horizontal Line Formula",
            type: "formulas",
            description: "Horizontal line: y = k (where k is the y-coordinate)",
            value: "y = k",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_y_coordinate",
            name: "Step 1: Identify Y-Coordinate",
            type: "steps",
            description: "Extract the y-coordinate from the point",
            value: "Step 1: y-coordinate = 5",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the y-coordinate as the constant",
            value: "Step 2: y = 5",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_verify_result",
            name: "Step 3: Verify Result",
            type: "steps",
            description: "Check that the point satisfies the equation",
            value: "Step 3: y = 5 ✓",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the horizontal line",
            value: "y = 5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point", to: "horizontal_line_formula", type: "used in", strength: 9 },
          { from: "horizontal_line_property", to: "horizontal_line_formula", type: "gives", strength: 9 },
          { from: "point", to: "step1_identify_y_coordinate", type: "used in", strength: 9 },
          { from: "horizontal_line_property", to: "step1_identify_y_coordinate", type: "used in", strength: 9 },
          { from: "horizontal_line_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_y_coordinate", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_verify_result", type: "leads to", strength: 10 },
          { from: "step3_verify_result", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'line_inter_01',
      problem: "Find the equation of a line passing through points (2, 1) and (4, 5)",
      problemType: "straight_line_equations",
      difficulty: "intermediate",
      description: "Line through two points",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point",
            value: "(2, 1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point",
            value: "(4, 5)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "slope_formula",
            name: "Slope Formula",
            type: "formulas",
            description: "Slope between two points: m = (y₂ - y₁)/(x₂ - x₁)",
            value: "m = (y₂ - y₁)/(x₂ - x₁)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_calculate_slope",
            name: "Step 1: Calculate Slope",
            type: "steps",
            description: "Find slope using two points",
            value: "Step 1: m = (5 - 1)/(4 - 2) = 4/2 = 2",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_point_slope",
            name: "Step 2: Apply Point-Slope",
            type: "steps",
            description: "Use point-slope form with calculated slope",
            value: "Step 2: y - 1 = 2(x - 2)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Expand and simplify to slope-intercept form",
            value: "Step 3: y - 1 = 2x - 4, y = 2x - 3",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the line",
            value: "y = 2x - 3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "slope_formula", type: "used in", strength: 9 },
          { from: "point2", to: "slope_formula", type: "used in", strength: 9 },
          { from: "point1", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "slope_formula", to: "step1_calculate_slope", type: "used in", strength: 10 },
          { from: "point1", to: "step1_calculate_slope", type: "used in", strength: 9 },
          { from: "point2", to: "step1_calculate_slope", type: "used in", strength: 9 },
          { from: "point_slope_formula", to: "step2_apply_point_slope", type: "used in", strength: 10 },
          { from: "step1_calculate_slope", to: "step2_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step2_apply_point_slope", to: "step3_simplify", type: "leads to", strength: 10 },
          { from: "step3_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_inter_02',
      problem: "Find the distance from point (3, 4) to the line 2x + y - 6 = 0",
      problemType: "straight_line_equations",
      difficulty: "intermediate",
      description: "Point to line distance",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Given point",
            value: "(3, 4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line_equation",
            name: "Line Equation",
            type: "concepts",
            description: "Line in standard form",
            value: "2x + y - 6 = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Point to line distance: d = |ax₀ + by₀ + c|/√(a² + b²)",
            value: "d = |ax₀ + by₀ + c|/√(a² + b²)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_coefficients",
            name: "Step 1: Identify Coefficients",
            type: "steps",
            description: "Extract a, b, c from line equation",
            value: "Step 1: a = 2, b = 1, c = -6",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_substitute_values",
            name: "Step 2: Substitute Values",
            type: "steps",
            description: "Substitute point and coefficients into formula",
            value: "Step 2: d = |2(3) + 1(4) - 6|/√(4 + 1)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: d = |6 + 4 - 6|/√5 = 4/√5 = 1.79",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The distance from point to line",
            value: "1.79 units",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point", to: "distance_formula", type: "used in", strength: 9 },
          { from: "line_equation", to: "distance_formula", type: "used in", strength: 9 },
          { from: "line_equation", to: "step1_identify_coefficients", type: "used in", strength: 9 },
          { from: "point", to: "step2_substitute_values", type: "used in", strength: 9 },
          { from: "distance_formula", to: "step2_substitute_values", type: "used in", strength: 10 },
          { from: "step1_identify_coefficients", to: "step2_substitute_values", type: "leads to", strength: 10 },
          { from: "step2_substitute_values", to: "step3_calculate", type: "leads to", strength: 10 },
          { from: "step3_calculate", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_inter_03',
      problem: "Find the angle between lines y = 2x + 1 and y = -x + 3",
      problemType: "straight_line_equations",
      difficulty: "intermediate",
      description: "Angle between two lines",
      graphData: {
        nodes: [
          {
            id: "line1",
            name: "Line 1",
            type: "concepts",
            description: "First line equation",
            value: "y = 2x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line2",
            name: "Line 2",
            type: "concepts",
            description: "Second line equation",
            value: "y = -x + 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle_formula",
            name: "Angle Formula",
            type: "formulas",
            description: "Angle between lines: tan(θ) = |(m₁ - m₂)/(1 + m₁m₂)|",
            value: "tan(θ) = |(m₁ - m₂)/(1 + m₁m₂)|",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_find_slopes",
            name: "Step 1: Find Slopes",
            type: "steps",
            description: "Extract slopes from line equations",
            value: "Step 1: m₁ = 2, m₂ = -1",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Substitute slopes into angle formula",
            value: "Step 2: tan(θ) = |(2 - (-1))/(1 + 2(-1))|",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_calculate_angle",
            name: "Step 3: Calculate Angle",
            type: "steps",
            description: "Calculate the angle using inverse tangent",
            value: "Step 3: tan(θ) = |3/(-1)| = 3, θ = tan⁻¹(3) = 71.57°",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The angle between lines",
            value: "71.57°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line1", to: "angle_formula", type: "used in", strength: 9 },
          { from: "line2", to: "angle_formula", type: "used in", strength: 9 },
          { from: "line1", to: "step1_find_slopes", type: "used in", strength: 9 },
          { from: "line2", to: "step1_find_slopes", type: "used in", strength: 9 },
          { from: "angle_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_find_slopes", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_calculate_angle", type: "leads to", strength: 10 },
          { from: "step3_calculate_angle", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_inter_04',
      problem: "Find the equation of a line perpendicular to y = 3x + 2 passing through point (1, 4)",
      problemType: "straight_line_equations",
      difficulty: "intermediate",
      description: "Perpendicular line equation",
      graphData: {
        nodes: [
          {
            id: "original_line",
            name: "Original Line",
            type: "concepts",
            description: "Given line equation",
            value: "y = 3x + 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perpendicular_slope_formula",
            name: "Perpendicular Slope Formula",
            type: "formulas",
            description: "Perpendicular slopes: m₂ = -1/m₁",
            value: "m₂ = -1/m₁",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_find_original_slope",
            name: "Step 1: Find Original Slope",
            type: "steps",
            description: "Extract slope from original line",
            value: "Step 1: m₁ = 3",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_find_perpendicular_slope",
            name: "Step 2: Find Perpendicular Slope",
            type: "steps",
            description: "Use perpendicular slope formula",
            value: "Step 2: m₂ = -1/3",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_apply_point_slope",
            name: "Step 3: Apply Point-Slope",
            type: "steps",
            description: "Use point-slope form with perpendicular slope",
            value: "Step 3: y - 4 = (-1/3)(x - 1)",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_simplify",
            name: "Step 4: Simplify",
            type: "steps",
            description: "Expand and simplify to slope-intercept form",
            value: "Step 4: y - 4 = (-1/3)x + 1/3, y = (-1/3)x + 13/3",
            importance: 9,
            x: 0, y: 150, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the perpendicular line",
            value: "y = (-1/3)x + 13/3",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "original_line", to: "perpendicular_slope_formula", type: "used in", strength: 9 },
          { from: "original_line", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "original_line", to: "step1_find_original_slope", type: "used in", strength: 9 },
          { from: "perpendicular_slope_formula", to: "step2_find_perpendicular_slope", type: "used in", strength: 10 },
          { from: "step1_find_original_slope", to: "step2_find_perpendicular_slope", type: "leads to", strength: 10 },
          { from: "point_slope_formula", to: "step3_apply_point_slope", type: "used in", strength: 10 },
          { from: "step2_find_perpendicular_slope", to: "step3_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step3_apply_point_slope", to: "step4_simplify", type: "leads to", strength: 10 },
          { from: "step4_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_inter_05',
      problem: "Find the intersection point of lines y = 2x + 1 and y = -x + 4",
      problemType: "straight_line_equations",
      difficulty: "intermediate",
      description: "Intersection of two lines",
      graphData: {
        nodes: [
          {
            id: "line1",
            name: "Line 1",
            type: "concepts",
            description: "First line equation",
            value: "y = 2x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line2",
            name: "Line 2",
            type: "concepts",
            description: "Second line equation",
            value: "y = -x + 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "intersection_formula",
            name: "Intersection Formula",
            type: "formulas",
            description: "Set y values equal and solve for x",
            value: "y₁ = y₂ → 2x + 1 = -x + 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_equate_y_values",
            name: "Step 1: Equate Y Values",
            type: "steps",
            description: "Set the y expressions equal to each other",
            value: "Step 1: 2x + 1 = -x + 4",
            importance: 10,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_solve_for_x",
            name: "Step 2: Solve for X",
            type: "steps",
            description: "Solve the linear equation for x",
            value: "Step 2: 3x = 3, x = 1",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_find_y",
            name: "Step 3: Find Y",
            type: "steps",
            description: "Substitute x back into either equation to find y",
            value: "Step 3: y = 2(1) + 1 = 3",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_write_point",
            name: "Step 4: Write Point",
            type: "steps",
            description: "Express as coordinate point",
            value: "Step 4: (1, 3)",
            importance: 9,
            x: 0, y: 150, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The intersection point",
            value: "(1, 3)",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "line1", to: "intersection_formula", type: "used in", strength: 9 },
          { from: "line2", to: "intersection_formula", type: "used in", strength: 9 },
          { from: "line1", to: "step1_equate_y_values", type: "used in", strength: 9 },
          { from: "line2", to: "step1_equate_y_values", type: "used in", strength: 9 },
          { from: "intersection_formula", to: "step2_solve_for_x", type: "used in", strength: 10 },
          { from: "step1_equate_y_values", to: "step2_solve_for_x", type: "leads to", strength: 10 },
          { from: "step2_solve_for_x", to: "step3_find_y", type: "enables", strength: 9 },
          { from: "line1", to: "step3_find_y", type: "used in", strength: 9 },
          { from: "step3_find_y", to: "step4_write_point", type: "leads to", strength: 10 },
          { from: "step4_write_point", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'line_hard_01',
      problem: "Find the equation of a line that is equidistant from points (1, 2) and (3, 4)",
      problemType: "straight_line_equations",
      difficulty: "harder",
      description: "Perpendicular bisector",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point",
            value: "(1, 2)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point",
            value: "(3, 4)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "midpoint_formula",
            name: "Midpoint Formula",
            type: "formulas",
            description: "Midpoint: M = ((x₁+x₂)/2, (y₁+y₂)/2)",
            value: "M = ((x₁+x₂)/2, (y₁+y₂)/2)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "perpendicular_slope_formula",
            name: "Perpendicular Slope Formula",
            type: "formulas",
            description: "Perpendicular slopes: m₂ = -1/m₁",
            value: "m₂ = -1/m₁",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_find_midpoint",
            name: "Step 1: Find Midpoint",
            type: "steps",
            description: "Calculate midpoint of the two points",
            value: "Step 1: M = ((1+3)/2, (2+4)/2) = (2, 3)",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_find_original_slope",
            name: "Step 2: Find Original Slope",
            type: "steps",
            description: "Calculate slope of line through the two points",
            value: "Step 2: m = (4-2)/(3-1) = 1",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_find_perpendicular_slope",
            name: "Step 3: Find Perpendicular Slope",
            type: "steps",
            description: "Use perpendicular slope formula",
            value: "Step 3: m_perp = -1/1 = -1",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_apply_point_slope",
            name: "Step 4: Apply Point-Slope",
            type: "steps",
            description: "Use point-slope form with midpoint and perpendicular slope",
            value: "Step 4: y - 3 = -1(x - 2)",
            importance: 10,
            x: 0, y: 150, z: 0
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Simplify to slope-intercept form",
            value: "Step 5: y - 3 = -x + 2, y = -x + 5",
            importance: 9,
            x: 0, y: 250, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the line",
            value: "y = -x + 5",
            importance: 10,
            x: 0, y: 350, z: 0
          }
        ],
        links: [
          { from: "point1", to: "midpoint_formula", type: "used in", strength: 9 },
          { from: "point2", to: "midpoint_formula", type: "used in", strength: 9 },
          { from: "point1", to: "perpendicular_slope_formula", type: "used in", strength: 9 },
          { from: "point2", to: "perpendicular_slope_formula", type: "used in", strength: 9 },
          { from: "point1", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "point2", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "midpoint_formula", to: "step1_find_midpoint", type: "used in", strength: 10 },
          { from: "point1", to: "step1_find_midpoint", type: "used in", strength: 9 },
          { from: "point2", to: "step1_find_midpoint", type: "used in", strength: 9 },
          { from: "perpendicular_slope_formula", to: "step2_find_original_slope", type: "used in", strength: 10 },
          { from: "point1", to: "step2_find_original_slope", type: "used in", strength: 9 },
          { from: "point2", to: "step2_find_original_slope", type: "used in", strength: 9 },
          { from: "step2_find_original_slope", to: "step3_find_perpendicular_slope", type: "leads to", strength: 10 },
          { from: "point_slope_formula", to: "step4_apply_point_slope", type: "used in", strength: 10 },
          { from: "step1_find_midpoint", to: "step4_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step3_find_perpendicular_slope", to: "step4_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step4_apply_point_slope", to: "step5_simplify", type: "leads to", strength: 10 },
          { from: "step5_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_hard_02',
      problem: "Find the area of the triangle formed by the lines y = x, y = 2x, and y = 3",
      problemType: "straight_line_equations",
      difficulty: "harder",
      description: "Triangle area from lines",
      graphData: {
        nodes: [
          {
            id: "line1",
            name: "Line 1",
            type: "concepts",
            description: "First line",
            value: "y = x",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line2",
            name: "Line 2",
            type: "concepts",
            description: "Second line",
            value: "y = 2x",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "line3",
            name: "Line 3",
            type: "concepts",
            description: "Third line",
            value: "y = 3",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "intersection_formula",
            name: "Intersection Formula",
            type: "formulas",
            description: "Set y values equal and solve for x",
            value: "y₁ = y₂ → solve for x",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "triangle_area_formula",
            name: "Triangle Area Formula",
            type: "formulas",
            description: "Area of triangle: A = ½ × base × height",
            value: "A = ½ × base × height",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_find_intersections",
            name: "Step 1: Find Intersections",
            type: "steps",
            description: "Find all intersection points of the three lines",
            value: "Step 1: (3,3), (1.5,3), (0,0)",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_identify_vertices",
            name: "Step 2: Identify Vertices",
            type: "steps",
            description: "Identify the three vertices of the triangle",
            value: "Step 2: Vertices at (0,0), (1.5,3), (3,3)",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_calculate_dimensions",
            name: "Step 3: Calculate Dimensions",
            type: "steps",
            description: "Calculate base and height of triangle",
            value: "Step 3: base = 1.5, height = 3",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_apply_area_formula",
            name: "Step 4: Apply Area Formula",
            type: "steps",
            description: "Use triangle area formula",
            value: "Step 4: A = ½ × 1.5 × 3 = 2.25",
            importance: 10,
            x: 0, y: 150, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the triangle",
            value: "2.25 square units",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "line1", to: "intersection_formula", type: "used in", strength: 9 },
          { from: "line2", to: "intersection_formula", type: "used in", strength: 9 },
          { from: "line3", to: "intersection_formula", type: "used in", strength: 9 },
          { from: "line1", to: "triangle_area_formula", type: "used in", strength: 9 },
          { from: "line2", to: "triangle_area_formula", type: "used in", strength: 9 },
          { from: "line3", to: "triangle_area_formula", type: "used in", strength: 9 },
          { from: "intersection_formula", to: "step1_find_intersections", type: "used in", strength: 10 },
          { from: "line1", to: "step1_find_intersections", type: "used in", strength: 9 },
          { from: "line2", to: "step1_find_intersections", type: "used in", strength: 9 },
          { from: "line3", to: "step1_find_intersections", type: "used in", strength: 9 },
          { from: "step1_find_intersections", to: "step2_identify_vertices", type: "leads to", strength: 10 },
          { from: "step2_identify_vertices", to: "step3_calculate_dimensions", type: "leads to", strength: 10 },
          { from: "triangle_area_formula", to: "step4_apply_area_formula", type: "used in", strength: 10 },
          { from: "step3_calculate_dimensions", to: "step4_apply_area_formula", type: "leads to", strength: 10 },
          { from: "step4_apply_area_formula", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_hard_03',
      problem: "Find the equation of a line that forms a 45° angle with the positive x-axis and passes through point (2, 1)",
      problemType: "straight_line_equations",
      difficulty: "harder",
      description: "Line with specific angle",
      graphData: {
        nodes: [
          {
            id: "angle",
            name: "Angle",
            type: "concepts",
            description: "Angle with x-axis",
            value: "45°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Point on the line",
            value: "(2, 1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "slope_angle_formula",
            name: "Slope-Angle Formula",
            type: "formulas",
            description: "Slope from angle: m = tan(θ)",
            value: "m = tan(θ)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_calculate_slope",
            name: "Step 1: Calculate Slope",
            type: "steps",
            description: "Use slope-angle formula to find slope",
            value: "Step 1: m = tan(45°) = 1",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_point_slope",
            name: "Step 2: Apply Point-Slope",
            type: "steps",
            description: "Use point-slope form with calculated slope",
            value: "Step 2: y - 1 = 1(x - 2)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Expand and simplify to slope-intercept form",
            value: "Step 3: y - 1 = x - 2, y = x - 1",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the line",
            value: "y = x - 1",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "angle", to: "slope_angle_formula", type: "used in", strength: 9 },
          { from: "point", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "angle", to: "step1_calculate_slope", type: "used in", strength: 9 },
          { from: "slope_angle_formula", to: "step1_calculate_slope", type: "used in", strength: 10 },
          { from: "point", to: "step2_apply_point_slope", type: "used in", strength: 9 },
          { from: "point_slope_formula", to: "step2_apply_point_slope", type: "used in", strength: 10 },
          { from: "step1_calculate_slope", to: "step2_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step2_apply_point_slope", to: "step3_simplify", type: "leads to", strength: 10 },
          { from: "step3_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_hard_04',
      problem: "Find the reflection of point (3, 4) over the line y = 2x + 1",
      problemType: "straight_line_equations",
      difficulty: "harder",
      description: "Point reflection over line",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Original point",
            value: "(3, 4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line",
            name: "Line",
            type: "concepts",
            description: "Line of reflection",
            value: "y = 2x + 1",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "perpendicular_slope_formula",
            name: "Perpendicular Slope Formula",
            type: "formulas",
            description: "Perpendicular slopes: m₂ = -1/m₁",
            value: "m₂ = -1/m₁",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "reflection_formula",
            name: "Reflection Formula",
            type: "formulas",
            description: "Reflection point: P' = 2M - P",
            value: "P' = 2M - P",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_find_perpendicular_slope",
            name: "Step 1: Find Perpendicular Slope",
            type: "steps",
            description: "Calculate slope perpendicular to reflection line",
            value: "Step 1: m_perp = -1/2",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_find_perpendicular_line",
            name: "Step 2: Find Perpendicular Line",
            type: "steps",
            description: "Find line through point perpendicular to reflection line",
            value: "Step 2: y - 4 = (-1/2)(x - 3)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_find_intersection",
            name: "Step 3: Find Intersection",
            type: "steps",
            description: "Find intersection of reflection line and perpendicular line",
            value: "Step 3: Solve y = 2x + 1 and y = (-1/2)x + 11/2",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_apply_reflection_formula",
            name: "Step 4: Apply Reflection Formula",
            type: "steps",
            description: "Use reflection formula to find reflected point",
            value: "Step 4: P' = 2M - P = (-1, 2)",
            importance: 10,
            x: 0, y: 150, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The reflected point",
            value: "(-1, 2)",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "line", to: "perpendicular_slope_formula", type: "used in", strength: 9 },
          { from: "point", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "line", to: "reflection_formula", type: "used in", strength: 9 },
          { from: "point", to: "reflection_formula", type: "used in", strength: 9 },
          { from: "line", to: "step1_find_perpendicular_slope", type: "used in", strength: 9 },
          { from: "perpendicular_slope_formula", to: "step1_find_perpendicular_slope", type: "used in", strength: 10 },
          { from: "point", to: "step2_find_perpendicular_line", type: "used in", strength: 9 },
          { from: "point_slope_formula", to: "step2_find_perpendicular_line", type: "used in", strength: 10 },
          { from: "step1_find_perpendicular_slope", to: "step2_find_perpendicular_line", type: "leads to", strength: 10 },
          { from: "line", to: "step3_find_intersection", type: "used in", strength: 9 },
          { from: "step2_find_perpendicular_line", to: "step3_find_intersection", type: "leads to", strength: 10 },
          { from: "reflection_formula", to: "step4_apply_reflection_formula", type: "used in", strength: 10 },
          { from: "step3_find_intersection", to: "step4_apply_reflection_formula", type: "leads to", strength: 10 },
          { from: "step4_apply_reflection_formula", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'line_hard_05',
      problem: "Find the equation of a line that is tangent to the circle x² + y² = 25 at point (3, 4)",
      problemType: "straight_line_equations",
      difficulty: "harder",
      description: "Tangent line to circle",
      graphData: {
        nodes: [
          {
            id: "circle_equation",
            name: "Circle Equation",
            type: "concepts",
            description: "Equation of the circle",
            value: "x² + y² = 25",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Point of tangency",
            value: "(3, 4)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "circle_formula",
            name: "Circle Formula",
            type: "formulas",
            description: "Standard circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "perpendicular_slope_formula",
            name: "Perpendicular Slope Formula",
            type: "formulas",
            description: "Perpendicular slopes: m₂ = -1/m₁",
            value: "m₂ = -1/m₁",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "point_slope_formula",
            name: "Point-Slope Formula",
            type: "formulas",
            description: "Point-slope form: y - y₁ = m(x - x₁)",
            value: "y - y₁ = m(x - x₁)",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify_center",
            name: "Step 1: Identify Center",
            type: "steps",
            description: "Find center of circle from equation",
            value: "Step 1: Center = (0, 0)",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_find_radius_slope",
            name: "Step 2: Find Radius Slope",
            type: "steps",
            description: "Calculate slope of radius from center to point",
            value: "Step 2: m_radius = (4-0)/(3-0) = 4/3",
            importance: 9,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_find_tangent_slope",
            name: "Step 3: Find Tangent Slope",
            type: "steps",
            description: "Use perpendicular slope formula",
            value: "Step 3: m_tangent = -1/m_radius = -3/4",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "step4_apply_point_slope",
            name: "Step 4: Apply Point-Slope",
            type: "steps",
            description: "Use point-slope form with tangent slope",
            value: "Step 4: y - 4 = (-3/4)(x - 3)",
            importance: 10,
            x: 0, y: 150, z: 0
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Expand and simplify to slope-intercept form",
            value: "Step 5: y - 4 = (-3/4)x + 9/4, y = (-3/4)x + 25/4",
            importance: 9,
            x: 0, y: 250, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The equation of the tangent line",
            value: "y = (-3/4)x + 25/4",
            importance: 10,
            x: 0, y: 350, z: 0
          }
        ],
        links: [
          { from: "circle_equation", to: "circle_formula", type: "used in", strength: 9 },
          { from: "point", to: "perpendicular_slope_formula", type: "used in", strength: 9 },
          { from: "point", to: "point_slope_formula", type: "used in", strength: 9 },
          { from: "circle_equation", to: "step1_identify_center", type: "used in", strength: 9 },
          { from: "circle_formula", to: "step1_identify_center", type: "used in", strength: 10 },
          { from: "point", to: "step2_find_radius_slope", type: "used in", strength: 9 },
          { from: "step1_identify_center", to: "step2_find_radius_slope", type: "leads to", strength: 10 },
          { from: "perpendicular_slope_formula", to: "step3_find_tangent_slope", type: "used in", strength: 10 },
          { from: "step2_find_radius_slope", to: "step3_find_tangent_slope", type: "leads to", strength: 10 },
          { from: "point", to: "step4_apply_point_slope", type: "used in", strength: 9 },
          { from: "point_slope_formula", to: "step4_apply_point_slope", type: "used in", strength: 10 },
          { from: "step3_find_tangent_slope", to: "step4_apply_point_slope", type: "leads to", strength: 10 },
          { from: "step4_apply_point_slope", to: "step5_simplify", type: "leads to", strength: 10 },
          { from: "step5_simplify", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 